import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { HydratedVideoItem, VideoManualConfig } from '@news-cms/shared';
import { groupContentsVideoPickerQueryOptions } from '@/lib/group-contents';
import { VideoPickerDialog } from './video-picker-dialog';
import { ReorderableItemList } from './reorderable-item-list';

interface Props {
  config: VideoManualConfig;
  maxCount: number;
  onChange: (next: VideoManualConfig) => void;
  /** Server-hydrated items keyed in admin order — preferred title/thumb
   *  source when present. */
  previewItems?: HydratedVideoItem[];
}

/**
 * Manual-video equivalent of ConfigEditorArticleManual. Shares the list
 * shell (count header, empty state, reorder/remove buttons) via
 * <ReorderableItemList>; only the per-row content + picker dialog differ.
 */
export function ConfigEditorVideoManual({ config, maxCount, onChange, previewItems }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [cache, setCache] = useState<Record<string, HydratedVideoItem>>({});

  // Same "common-case" hydration trick as article-manual — fetch the
  // latest 30 videos so already-picked ones likely have titles ready.
  const lookupQuery = useQuery({
    ...groupContentsVideoPickerQueryOptions('', null),
    staleTime: 60_000,
  });
  useEffect(() => {
    if (!lookupQuery.data) return;
    setCache((prev) => {
      const next = { ...prev };
      for (const v of lookupQuery.data) if (!next[v.id]) next[v.id] = v;
      return next;
    });
  }, [lookupQuery.data]);
  // Server-hydrated preview wins over picker cache.
  useEffect(() => {
    if (!previewItems) return;
    setCache((prev) => {
      const next = { ...prev };
      for (const v of previewItems) next[v.id] = v;
      return next;
    });
  }, [previewItems]);

  const addVideo = (video: HydratedVideoItem) => {
    if (config.items.some((it) => it.videoId === video.id)) return;
    if (config.items.length >= maxCount) return;
    setCache((prev) => ({ ...prev, [video.id]: video }));
    onChange({ ...config, items: [...config.items, { videoId: video.id }] });
  };

  return (
    <>
      <ReorderableItemList
        items={config.items}
        maxCount={maxCount}
        addLabel="เพิ่มวิดีโอ"
        emptyText='ยังไม่มีวิดีโอ — กดปุ่ม "เพิ่มวิดีโอ" เพื่อเลือก'
        onAddClick={() => setPickerOpen(true)}
        onChange={(items) => onChange({ ...config, items })}
        getKey={(it, idx) => `${it.videoId}-${idx}`}
        renderItem={(it) => {
          const video = cache[it.videoId];
          return (
            <>
              {video ? (
                <img
                  src={video.thumbnailUrl}
                  alt=""
                  className="h-10 w-16 shrink-0 rounded object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.visibility = 'hidden';
                  }}
                />
              ) : (
                <div className="h-10 w-16 shrink-0 rounded bg-slate-100 dark:bg-slate-800" />
              )}
              <div className="min-w-0 flex-1">
                <div className="line-clamp-1 text-sm font-medium text-slate-900 dark:text-slate-50">
                  {video?.title ?? <code className="text-xs">{it.videoId}</code>}
                </div>
                {video && (
                  <div className="text-[11px] text-slate-400">{video.channelTitle ?? '—'}</div>
                )}
              </div>
            </>
          );
        }}
      />

      <VideoPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        alreadyPicked={config.items.map((it) => it.videoId)}
        onPick={addVideo}
      />
    </>
  );
}
